import { useCallback, useEffect, useRef, useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { optimizedImage } from "@/lib/image-url";
import { VideoPlayer } from "@/components/video-player";
import type { PostMedia } from "@/components/post-card";
import { useAuth } from "@/lib/auth-context";
import { canWatch, normalizeReviewState, useIsMediaReviewer } from "@/lib/media-review";
import { MediaReviewBadge, MediaReviewPlaceholder } from "@/components/media-review-badge";

export function MediaLightbox({
  media,
  startIndex,
  open,
  onOpenChange,
  ownerId,
}: {
  media: PostMedia[];
  startIndex: number;
  open: boolean;
  onOpenChange: (o: boolean) => void;
  ownerId?: string | null;
}) {
  const { user } = useAuth();
  const isReviewer = useIsMediaReviewer();
  const isOwner = !!user && !!ownerId && user.id === ownerId;
  const [idx, setIdx] = useState(startIndex);
  const [zoom, setZoom] = useState(false);
  const [drag, setDrag] = useState(0);
  const swipe = useRef<{ x: number; y: number; id: number } | null>(null);

  useEffect(() => { if (open) { setIdx(startIndex); setZoom(false); setDrag(0); } }, [open, startIndex]);

  const go = useCallback((dir: 1 | -1) => {
    setIdx((i) => Math.min(media.length - 1, Math.max(0, i + dir)));
    setZoom(false);
    setDrag(0);
  }, [media.length]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
      if (e.key === "Escape") onOpenChange(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, go, onOpenChange]);

  // Horizontal swipe moves between items; a mostly-vertical drag is ignored so
  // pinch-zoomed panning and scrolling still behave.
  const onPointerDown = (e: React.PointerEvent) => {
    if (e.pointerType === "mouse" || zoom) return;
    swipe.current = { x: e.clientX, y: e.clientY, id: e.pointerId };
  };
  const onPointerMove = (e: React.PointerEvent) => {
    const s = swipe.current;
    if (!s || s.id !== e.pointerId) return;
    const dx = e.clientX - s.x;
    if (Math.abs(dx) < Math.abs(e.clientY - s.y)) return;
    setDrag(dx);
  };
  const endSwipe = (e: React.PointerEvent) => {
    const s = swipe.current;
    swipe.current = null;
    if (!s || s.id !== e.pointerId) return;
    const dx = e.clientX - s.x;
    if (Math.abs(dx) > 60) go(dx < 0 ? 1 : -1);
    else setDrag(0);
  };

  const current = media[idx];
  if (!current) return null;


  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[100vw] sm:max-w-[95vw] h-[100dvh] sm:h-[92vh] p-0 bg-black/95 border-0 rounded-none sm:rounded-xl overflow-hidden">
        <button
          onClick={() => onOpenChange(false)}
          className="absolute right-3 top-3 z-50 grid h-9 w-9 place-items-center rounded-full bg-white/10 text-white backdrop-blur transition-colors hover:bg-white/20"
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>

        {idx > 0 && (
          <button
            onClick={() => go(-1)}
            className="absolute left-3 top-1/2 z-40 hidden h-11 w-11 -translate-y-1/2 place-items-center rounded-full bg-white/10 text-white backdrop-blur hover:bg-white/20 sm:grid"
            aria-label="Previous"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
        )}
        {idx < media.length - 1 && (
          <button
            onClick={() => go(1)}
            className="absolute right-3 top-1/2 z-40 hidden h-11 w-11 -translate-y-1/2 place-items-center rounded-full bg-white/10 text-white backdrop-blur hover:bg-white/20 sm:grid"
            aria-label="Next"
          >
            <ChevronRight className="h-6 w-6" />
          </button>
        )}

        <div
          className="grid h-full w-full touch-pan-y place-items-center overflow-hidden"
          onClick={() => onOpenChange(false)}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endSwipe}
          onPointerCancel={endSwipe}
          style={{
            transform: drag ? `translateX(${drag * 0.4}px)` : undefined,
            transition: drag ? "none" : "transform 220ms ease-out",
          }}
        >
          {current.media_type === "video" ? (
            <div key={current.id} className="flex h-full w-full flex-col" onClick={(e) => e.stopPropagation()}>
              {(() => {
                const review = normalizeReviewState(current.review_state);
                const watchable = canWatch(review, { isOwner, isReviewer });
                return (
                  <>
                    <div className="min-h-0 flex-1">
                      {watchable ? (
                        <VideoPlayer
                          source={{ id: current.id, url: current.url, posterUrl: current.poster_url, durationSeconds: current.duration_seconds, scope: "post" }}
                          objectFit="contain"
                          eager
                        />
                      ) : (
                        <MediaReviewPlaceholder state={review} className="bg-white/5 text-white" />
                      )}
                    </div>
                    {review !== "approved" && (
                      <div className="flex justify-center px-4 pb-16 pt-3">
                        <MediaReviewBadge state={review} scope="post" id={current.id} compact />
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          ) : (
            <img
              key={current.id}
              src={zoom ? current.url : optimizedImage(current.url, 1600, 80)}
              alt=""
              decoding="async"
              fetchPriority="high"
              onClick={(e) => { e.stopPropagation(); setZoom((z) => !z); }}
              onDoubleClick={(e) => { e.stopPropagation(); setZoom((z) => !z); }}
              className={cn(
                "max-h-full max-w-full transition-transform duration-200 cursor-zoom-in select-none",
                zoom && "scale-150 cursor-zoom-out"
              )}
              draggable={false}
            />
          )}
        </div>


        {media.length > 1 && (
          <div className="absolute bottom-4 left-1/2 z-40 -translate-x-1/2 rounded-full bg-white/10 px-3 py-1 text-xs font-medium text-white backdrop-blur">
            {idx + 1} / {media.length}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
